import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { authService } from '@/services/auth.service'
import { organizationService } from '@/services/organization.service'
import type { User as DbUser } from '@/types'
import { supabase } from '@/lib/supabaseClient'

interface AuthContextType {
  user: User | null
  session: Session | null
  dbUser: DbUser | null
  isLoading: boolean
  isAuthenticated: boolean
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [dbUser, setDbUser] = useState<DbUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchDbUser = async (userId: string) => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single()

    if (error) {
      console.error('Error fetching user profile:', error)
      return null
    }

    return data
  }

  const ensureUserSetupAndFetch = async () => {
    // This will create the organization and user profile if they don't exist
    const { userProfile } = await organizationService.ensureUserSetup()
    return userProfile
  }

  const refreshUser = async () => {
    const { user } = await authService.getUser()
    if (user) {
      setUser(user)
      // First try to fetch existing profile
      let profile = await fetchDbUser(user.id)
      // If no profile exists, ensure setup and try again
      if (!profile) {
        profile = await ensureUserSetupAndFetch()
      }
      setDbUser(profile)
    }
  }

  useEffect(() => {
    // Get initial session
    const initAuth = async () => {
      try {
        const { session } = await authService.getSession()
        setSession(session)
        setUser(session?.user ?? null)

        if (session?.user) {
          // First try to fetch existing profile
          let profile = await fetchDbUser(session.user.id)
          // If no profile or no organization, ensure setup
          if (!profile || !profile.organization_id) {
            profile = await ensureUserSetupAndFetch()
          }
          setDbUser(profile)
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initAuth()

    // Subscribe to auth changes
    const { data: { subscription } } = authService.onAuthStateChange(
      async (event, session) => {
        setSession(session)
        setUser(session?.user ?? null)

        if (session?.user) {
          // First try to fetch existing profile
          let profile = await fetchDbUser(session.user.id)
          // If no profile or no organization, ensure setup (e.g., new signup)
          if (!profile || !profile.organization_id) {
            profile = await ensureUserSetupAndFetch()
          }
          setDbUser(profile)
        } else {
          setDbUser(null)
        }

        setIsLoading(false)
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const handleSignOut = async () => {
    await authService.signOut()
    setUser(null)
    setSession(null)
    setDbUser(null)
  }

  const value: AuthContextType = {
    user,
    session,
    dbUser,
    isLoading,
    isAuthenticated: !!session,
    signOut: handleSignOut,
    refreshUser
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
